import { useState, useEffect, useCallback } from 'react';
import { Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Checkbox from 'src/components/Checkbox';
import { useTextStyles } from 'src/hooks/useTextStyles';

export const useStyles = makeStyles(() =>
  createStyles({
    selectChildren: {
      marginLeft: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      position: 'relative',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: -8,
        width: 1,
        top: 4,
        bottom: 4,
        borderLeft: '1px dashed gray',
      },
    },
    selectionContainer: {
      width: 280,
      padding: '4px 8px',
    },
    selectionOptions: {
      maxHeight: 250,
      overflow: 'auto',

      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
      },
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track ': {
        background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },
    },
    selectOption: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,

      '& > svg': {
        width: 16,
        height: 16,
        cursor: 'pointer',
      },
    },
    searchContainer: {
      maxWidth: 280,
      display: 'flex',
      alignItems: 'center',
      padding: 4,
      border: '1px solid #C3C3C7',
      borderRadius: 20,
      gap: 4,
      transitionProperty: 'all',
      transitionTimingFunction: 'linear',
      transitionDuration: '250ms',
      marginBottom: 8,
      marginRight: 12,

      '& > svg': {
        width: 16,
        height: 16,
      },

      '& > input': {
        height: 16,
        border: 'none',
        padding: 0,
        margin: 0,
        flex: 1,
        outline: 'none',
        fontSize: 12,
      },
    },
    category: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        fontSize: 20,
        cursor: 'pointer',
      },
    },
    categoryContent: {
      marginLeft: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      position: 'relative',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: -11,
        width: 1,
        top: 4,
        bottom: 4,
        borderLeft: '1px dashed gray',
      },
    },
  })
);

interface TreeSelectionProps {
  title: string;
  selections: any[];
  selected: any[];
  handleSave: (selected: any[]) => void;
}

export const TreeSelection = ({
  title,
  selections,
  selected,
  handleSave,
}: TreeSelectionProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const [data, setData] = useState<any[]>([]);
  const [searchKey, setSearchKey] = useState<string>('');

  const formatTreeData = useCallback(
    (tree: any): any[] => {
      const temp: any[] = [];
      tree
        .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        .forEach((item: any) => {
          const children =
            item.children && item.children.length
              ? formatTreeData(item.children)
              : [];
          temp.push({
            ...item,
            isChecked: !!selected.find((category) => category.id === item.id),
            isExpanded: false,
            isShow: true,
            children,
          });
        });
      return temp;
    },
    [selected]
  );

  const filterTreeData = useCallback(
    (tree: any, key: string, isParentShow = false): any[] => {
      const temp: any[] = [];
      tree.forEach((item: any) => {
        const isShow = item.name.toLowerCase().includes(key.toLowerCase());
        const children =
          item.children && item.children.length
            ? filterTreeData(item.children, key, isShow || isParentShow)
            : [];

        temp.push({
          ...item,
          isChecked: !!selected.find((category) => category.id === item.id),
          isShow:
            key === '' ||
            isShow ||
            isParentShow ||
            children.find((child) => child.isShow)
              ? true
              : false,
          children,
        });
      });
      return temp;
    },
    [selected]
  );

  const countShowData = useCallback(
    (tree: any): number => {
      let counter = 0;
      tree.forEach((item: any) => {
        const childCounter =
          item.children && item.children.length
            ? countShowData(item.children)
            : 0;
        const currentCounter = childCounter
          ? childCounter
          : item.isShow
          ? 1
          : 0;
        counter = counter + currentCounter;
      });
      return counter;
    },
    [selected]
  );

  useEffect(() => {
    setData(filterTreeData(data, searchKey || ''));
  }, [searchKey, filterTreeData]);

  const saveSelection = useCallback((tree: any[]) => {
    let temp: any[] = [];
    tree.forEach((item: any) => {
      if (item.isChecked) {
        temp.push(item);
      }
      if (item.children && item.children.length) {
        const children = saveSelection(item.children);
        temp = [...temp, ...children];
      }
    });
    return temp;
  }, []);

  useEffect(() => {
    setData(formatTreeData(selections));
  }, [selections]);

  const updateTreeData = useCallback(
    (
      tree: any[],
      id: string,
      property: string,
      value: string | boolean
    ): any[] => {
      const temp: any[] = [...tree];
      tree.forEach((node, index) => {
        if (node.id === id) {
          temp[index][property] = value;
          if (
            property === 'isChecked' &&
            node.children &&
            node.children.length
          ) {
            const children = node.children.map((child: any) => {
              updateTreeData(node.children, child.id, property, value);
              return {
                ...child,
                isChecked: value,
              };
            });
            temp[index].children = children;
          }
        } else {
          if (node.children && node.children.length) {
            const children = updateTreeData(node.children, id, property, value);
            let isChecked = node.isChecked;
            if (property === 'isChecked') {
              isChecked = children.every((child) => child.isChecked);
            }
            temp[index] = {
              ...node,
              children,
              isChecked,
            };
          }
        }
      });
      return temp;
    },
    []
  );

  const showCategories = (categories: any[]) => {
    return categories.map((item: any) => {
      const hasChildren = !!item.children?.length;
      if (!item.isShow) {
        return null;
      }

      return (
        <div key={item.id}>
          <div className={classes.selectOption}>
            {hasChildren ? (
              item.isExpanded ? (
                <KeyboardArrowDownIcon
                  onClick={() => {
                    setData(updateTreeData(data, item.id, 'isExpanded', false));
                  }}
                />
              ) : (
                <KeyboardArrowRightIcon
                  onClick={() => {
                    setData(updateTreeData(data, item.id, 'isExpanded', true));
                  }}
                />
              )
            ) : (
              <div style={{ width: 16 }} />
            )}
            <Checkbox
              name={item.id}
              label=''
              checked={item.isChecked}
              noPadding={true}
              onChange={() => {
                const newData = updateTreeData(
                  data,
                  item.id,
                  'isChecked',
                  !item.isChecked
                );
                setData(newData);
                handleSave(saveSelection(newData));
              }}
            />
            <Typography
              style={{
                fontSize: 14,
                fontWeight: 'normal',
              }}
            >
              {item.name} {hasChildren ? <>({item.children?.length})</> : null}
            </Typography>
          </div>
          <div className={classes.selectChildren}>
            {hasChildren && item.isExpanded && showCategories(item.children)}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={classes.selectionContainer} style={{ width: '100%' }}>
      <Typography
        className={textClasses.xxsRegular}
        style={{ marginBottom: 8 }}
      >
        Select {title}
      </Typography>
      <div className={classes.searchContainer}>
        <SearchIcon />
        <input
          placeholder={`Search ${title}...`}
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
        />
      </div>
      <div className={classes.selectionOptions}>{showCategories(data)}</div>
    </div>
  );
};
